import { hookToAttributeSelector } from '@wix/panda-js-utils';
import { SEAT_SPACING, SEAT_CIRCLE_DIAMETER } from '../../constants';
import { SHAPE } from '../../interfaces';
import { getNextElementId } from '../../utils/id-generator';
import { getElement } from '../../utils/utils';
import { DH } from '../../data-hooks';
import { getFactualElementPosition } from '../../utils/bounding-boxes';
import { createLabelGenerator } from '../../utils/label-generator';
import { getPlaceElementId } from '../../utils/place';
export const getSeatsAmount = (size, spacing = SEAT_SPACING) => {
    const reservedSeatSpace = spacing + SEAT_CIRCLE_DIAMETER;
    const amount = size < SEAT_CIRCLE_DIAMETER ? 0 : Math.floor(size / reservedSeatSpace) + 1;
    return amount;
};
export const getSeatsLength = (seats, spacing = SEAT_SPACING) => {
    return SEAT_CIRCLE_DIAMETER * seats + spacing * (seats - 1);
};
// TODO: remove once unused (probably when multirow is merged)
/** @deprecated DO NOT USE IT IN ANY NEW PLACES OF CODE*/
export const getSeatCapacity = (definition) => {
    const { width, height, spacing = SEAT_SPACING, spacingBetweenRows = SEAT_SPACING } = definition;
    const seatsInRow = Array.isArray(width) || Array.isArray(spacing) ? null : getSeatsAmount(width, spacing);
    const rowsAmount = Array.isArray(height) || Array.isArray(spacingBetweenRows) ? null : getSeatsAmount(height, spacingBetweenRows);
    return {
        /** @deprecated */
        seatsInRow,
        /** @deprecated */
        rowsAmount,
    };
};
export const getRows = ({ definition, absolutePosition = false, shapeContainer, actionContainer, svgViewBox, }) => {
    const { width, startAt, labelPosition, reverseDirection, sectorLabel, sequence, spacing = SEAT_SPACING, spacingBetweenRows: rowSpacing = SEAT_SPACING, shapeBorderColor, shapeColor, shapeTextColor, rowsSequence, rowsStartAt, rowsReverseDirection, groupId, } = definition;
    const verticalReservedSeatSpace = rowSpacing + SEAT_CIRCLE_DIAMETER;
    const rowsAmount = getSeatsAmount(definition.height, definition.spacingBetweenRows);
    const rows = [];
    let rowPositions = [];
    if (absolutePosition) {
        if (shapeContainer) {
            rowPositions = getActualRowPositions(definition, shapeContainer, svgViewBox) ?? [];
        }
        if (!rowPositions.length && actionContainer) {
            rowPositions = getActualRowPositions(definition, actionContainer, svgViewBox) ?? [];
        }
    }
    const rowLabelGenerator = createLabelGenerator({
        sequence: rowsSequence,
        startAt: rowsStartAt,
        reverseDirection: rowsReverseDirection,
        capacity: rowsAmount,
    });
    // if specs.seatings.ui.MultiRow is enabled, definition will always contain rows
    if (definition.rows?.length && definition.capacity) {
        const rowPlaceMap = {};
        for (const place of definition.places ?? []) {
            const shapeId = getPlaceElementId(place);
            rowPlaceMap[shapeId] = rowPlaceMap[shapeId] ?? [];
            rowPlaceMap[shapeId].push(place);
        }
        definition.rows.forEach((row, rowIndex) => {
            const capacity = getSeatsAmount(row.overrides.width ?? width, row.overrides.spacing ?? spacing);
            const defaultRowLabel = rowLabelGenerator.getLabelByIndex(rowIndex);
            const rowDefinition = {
                id: row.id,
                type: SHAPE.ROW,
                angle: 0,
                x: row.overrides?.relativeX ?? 0,
                y: rowIndex * verticalReservedSeatSpace,
                width: row.overrides.width ?? width,
                height: SEAT_CIRCLE_DIAMETER,
                startAt: row.overrides?.startAt ?? startAt,
                label: row.overrides?.label ?? defaultRowLabel,
                labelPosition: row.overrides?.labelPosition ?? labelPosition,
                reverseDirection: row.overrides?.reverseDirection ?? reverseDirection,
                capacity,
                sectorLabel,
                sequence: row.overrides?.sequence ?? sequence,
                spacing: row.overrides?.spacing ?? spacing,
                shapeBorderColor: row.shapeBorderColor ?? shapeBorderColor,
                shapeColor: row.shapeColor ?? shapeColor,
                shapeTextColor: row.shapeTextColor ?? shapeTextColor,
                groupId,
                places: rowPlaceMap[row.id] ?? [],
                categoryId: definition?.categoryId,
                placeOverrides: row.placeOverrides ?? [],
            };
            if (absolutePosition) {
                rowDefinition.x = rowPositions[rows.length].x - definition.x;
                rowDefinition.y = rowPositions[rows.length].y - definition.y;
                rowDefinition.angle = definition.angle;
            }
            rows.push(rowDefinition);
        });
    }
    else {
        const { seatsInRow } = getSeatCapacity(definition);
        for (let rowIndex = 0; rowIndex < rowsAmount; rowIndex++) {
            const row = {
                id: absolutePosition ? getNextElementId() : rowIndex,
                type: SHAPE.ROW,
                angle: 0,
                x: 0,
                y: rowIndex * verticalReservedSeatSpace,
                width,
                height: SEAT_CIRCLE_DIAMETER,
                startAt,
                label: rowLabelGenerator.getLabelByIndex(rowIndex),
                labelPosition,
                reverseDirection,
                capacity: seatsInRow,
                sectorLabel,
                sequence,
                spacing,
                shapeBorderColor,
                shapeColor,
                shapeTextColor,
                groupId,
                places: [],
                placeOverrides: [],
            };
            if (absolutePosition) {
                row.x = rowPositions[rows.length].x;
                row.y = rowPositions[rows.length].y;
                row.angle = definition.angle;
            }
            rows.push(row);
        }
    }
    return rows;
};
// TODO: remove when merging specs.seatings.ui.MultiRow
const getActualRowPositions = (definition, shapeContainer, svgViewBox) => {
    if (!shapeContainer && !shapeContainer.parentElement) {
        throw new Error('Cannot calculate absolute position of rows without having a reference to main SVG container');
    }
    const multiRowElement = getElement(definition.id, shapeContainer.parentElement, undefined);
    if (!multiRowElement) {
        return null;
    }
    const rowNodeList = multiRowElement.querySelectorAll(hookToAttributeSelector(DH.MULTI_ROW_ROW));
    const rowRootElements = [];
    rowNodeList.forEach(node => rowRootElements.push(node));
    const rowSeatWrapperElements = rowRootElements.map(root => {
        const seats = root.querySelector(hookToAttributeSelector(DH.MULTI_ROW_ROW_SEATS));
        return seats.querySelector('[data-measure-during-apply]') || seats;
    });
    const positions = rowSeatWrapperElements.map((el, index) => {
        // TODO: always take from overrides when MultiRow is merged
        const width = definition.rows?.[index]?.overrides?.width ?? definition.width;
        return getFactualElementPosition({
            element: el,
            definition: { ...definition, height: SEAT_CIRCLE_DIAMETER, width },
            container: shapeContainer,
            svgViewBox,
        });
    });
    return positions;
};
//# sourceMappingURL=utils.js.map